import React, { FunctionComponent, useState, useEffect, Fragment } from 'react';
import Router from 'next/router';
import { ButtonLink, Text } from 'mdlkit';
import { withLoginExperience } from '../../../utils/authExperienceNavigation';
import { Modal } from '../../../core';
import AffiliationForm from './AffiliationForm';
import { PatientAffiliation } from '../../../interfaces/register';
import getPatientAffiliationsAsync from '../../../api/patients/getPatientAffiliationsAsync';
import {
  LOCKED_STATUS,
  SIGN_IN_CHALLENGE_URL,
  PASSWORD_RESET_URL,
} from '../../../constants/login';
import toast from ' ../../../utils/toast';
import { event } from '../../../lib/analytics';
import { REGISTRATION } from '../../../constants/events';

interface Props {
  email: string;
  birthdate: string;
}

const DuplicateUserError: FunctionComponent<Props> = ({
  email,
  birthdate,
}: Props) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [affiliations, setAffiliations] = useState<PatientAffiliation[] | null>(
    null
  );

  useEffect(() => {
    const setPatientAffiliations = async () => {
      const {
        affiliations: patientAffiliations,
      } = await getPatientAffiliationsAsync({ email, birthdate });
      setAffiliations(patientAffiliations);
    };

    setPatientAffiliations();
  }, []);

  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  const onSubmit = ({ affiliationId }: { affiliationId: string }) => {
    if (affiliations) {
      const selectedAffiliation = affiliations.find(
        (affiliation) => affiliation.id.toString() === affiliationId
      );
      if (typeof selectedAffiliation === 'undefined') return;

      event({
        action: 'select_affiliation_multiple_accounts',
        category: REGISTRATION,
        parameters: { affiliationId },
      });

      const { user } = selectedAffiliation;

      if (user.status === LOCKED_STATUS) {
        Router.push(
          withLoginExperience(
            `${PASSWORD_RESET_URL}?birthdate=${encodeURIComponent(
              birthdate
            )}&email=${encodeURIComponent(
              email
            )}&affiliationId=${affiliationId}`,
            Router
          )
        );
      } else {
        Router.push(
          withLoginExperience(
            `${SIGN_IN_CHALLENGE_URL}?birthdate=${encodeURIComponent(
              birthdate
            )}&email=${encodeURIComponent(
              email
            )}&affiliationId=${affiliationId}`,
            Router
          )
        );
      }
      close();
      toast.dismiss();
    }
  };

  return (
    <Fragment>
      You have multiple accounts with that email and date of birth. Please{' '}
      <ButtonLink
        onClick={() => {
          open();
          event({ action: 'click_recover_account', category: REGISTRATION });
        }}
      >
        Click Here
      </ButtonLink>{' '}
      to recover your account.
      {affiliations ? (
        <Modal
          isOpen={showDialog}
          onDismiss={close}
          ariaLabel="Instructions to recover account"
        >
          <Modal.Header>Choose an account</Modal.Header>
          <Modal.Content>
            <Text>
              To help recover your account, please select your current benefit
              provider.
            </Text>

            <AffiliationForm affiliations={affiliations} onSubmit={onSubmit} />
          </Modal.Content>
        </Modal>
      ) : null}
    </Fragment>
  );
};

export default DuplicateUserError;
